import React from 'react';

import { parse } from '../../../util/urlHelpers';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { FieldLocationAutocompleteInput } from '../../../components';
import { setUserLocation, radiusToBounds } from '../../../util/analytics';
import { US_STATE_ABBR, SEARCH_RADIUS_MILES } from '../../../util/search';

import FilterPlain from '../FilterPlain/FilterPlain';

import css from './LocationFilter.module.css';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

// The QUERY box is SEARCH_RADIUS_MILES (50) wide; SearchResultsPanel splits
// results at NEAR_RADIUS_MILES (25) into "your area" vs "a bit farther away"
// (docs/design/search-extended-radius.md). A renter-adjustable radius and
// per-kit delivery-radius matching remain follow-ups.
const RADIUS_MILES = SEARCH_RADIUS_MILES;

// Build an SDKLatLngBounds box centered on lat/lng, reusing the existing
// radiusToBounds helper (returns { ne: 'lat,lng', sw: 'lat,lng' }).
const boundsForCenter = (lat, lng) => {
  const { ne, sw } = radiusToBounds(lat, lng, RADIUS_MILES);
  const [neLat, neLng] = ne.split(',').map(Number);
  const [swLat, swLng] = sw.split(',').map(Number);
  return new SDKLatLngBounds(new SDKLatLng(neLat, neLng), new SDKLatLng(swLat, swLng));
};


// Compact label for the closed pill: drop the redundant ", United States"
// (US-only marketplace), abbreviate the state, and ellipsis-cap so the value
// never overflows the pill. e.g. "Goleta, California 93117, United States"
// -> "Goleta, CA 93117".
const MAX_LABEL_CHARS = 28;
const shortLocationLabel = address => {
  if (!address) return '';
  const noCountry = address.replace(/,?\s*United States(?: of America)?\s*$/i, '').trim();
  // The geocoder formats as "City, State ZIP" — the state lives in the last
  // comma-segment. Abbreviate only there, so a city that shares a state's
  // name (Washington DC, Virginia Beach) isn't mangled. Longest names first
  // so "West Virginia" isn't clobbered by "Virginia".
  const parts = noCountry.split(',');
  const lastIdx = parts.length - 1;
  if (lastIdx >= 1) {
    const hit = Object.keys(US_STATE_ABBR)
      .sort((a, b) => b.length - a.length)
      .find(name => new RegExp(`\\b${name}\\b`).test(parts[lastIdx]));
    if (hit) {
      parts[lastIdx] = parts[lastIdx].replace(new RegExp(`\\b${hit}\\b`), US_STATE_ABBR[hit]);
    }
  }
  const s = parts.join(',').trim();
  return s.length > MAX_LABEL_CHARS ? `${s.slice(0, MAX_LABEL_CHARS - 1).trimEnd()}…` : s;
};

/**
 * Must-have "Location" filter for the search sidebar. A FilterPlain shell
 * (inherits the outside-click-to-close behavior) hosting a Mapbox location
 * autocomplete + a "use my current location" link. Selecting a place sets the
 * `bounds` URL param so Sharetribe filters results to that area.
 *
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.name] - form field name (default 'location')
 * @param {React.Node} [props.label]
 * @param {Object} props.location - router location (reads the active `address`)
 * @param {Function} props.onLocationChange - called with { bounds, address }
 *   (bounds null clears the filter)
 */
const LocationFilter = props => {
  const {
    id = 'LocationFilter',
    name = 'location',
    label = 'Location',
    location,
    onLocationChange,
    ...rest
  } = props;

  // Active location label comes from the URL `address` param (set when a
  // location is applied), so it survives reloads and shared links.
  const queryParams = parse(location?.search || '');
  const activeAddress = typeof queryParams.address === 'string' ? queryParams.address : '';
  const hasActive = activeAddress.length > 0;

  // Cache the center for analytics geo + push the bounds box to the URL.
  const applyCenter = (lat, lng, address) => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    setUserLocation({ lat, lng, city: address, radius: RADIUS_MILES });
    onLocationChange({ bounds: boundsForCenter(lat, lng), address });
  };

  // FilterPlain liveEdit fires onSubmit on every change; act only once a
  // prediction with an origin has actually been selected.
  const handleSubmit = values => {
    const selected = values?.[name]?.selectedPlace;
    const origin = selected?.origin;
    if (origin && typeof origin.lat === 'number' && typeof origin.lng === 'number') {
      applyCenter(origin.lat, origin.lng, selected.address || values?.[name]?.search || '');
    }
  };

  const handleClear = () => {
    onLocationChange({ bounds: null, address: null });
  };

  return (
    <FilterPlain
      className={css.root}
      label={label}
      labelSelection={hasActive ? shortLocationLabel(activeAddress) : null}
      labelSelectionSeparator=":"
      isSelected={hasActive}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      onClear={handleClear}
      initialValues={{ [name]: { search: activeAddress } }}
      {...rest}
    >
      <div className={css.field}>
        <FieldLocationAutocompleteInput
          rootClassName={css.location}
          inputClassName={css.locationInput}
          iconClassName={css.locationIcon}
          predictionsClassName={css.locationPredictions}
          name={name}
          placeholder="City or zip code"
          useDefaultPredictions={false}
          format={v => v}
        />
      </div>
    </FilterPlain>
  );
};

export default LocationFilter;
